import { useId, type ReactNode } from 'react';
import {
  ButtonLink,
  Card,
  CardBody,
  CardHeader,
  Link,
  Text,
} from '@backstage/ui';
import { iconsApiRef, useApi } from '@backstage/frontend-plugin-api';
import type { AppProfile } from '@internal/plugin-waadoo-config-common';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { ActionIcon } from '../projectEnvironments/ActionIcon';
import { ReauthAlert } from '../projectEnvironments/ReauthAlert';
import {
  pipelineNumber,
  type PipelineInfo,
} from '../projectEnvironments/pipelines';
import { buildDurationMs } from '../projectEnvironments/serviceProgress';
import type { ArgoApplication } from '../projectEnvironments/argoApp';
import { applicationUrlOf, useAppLogoKeys } from '../shared';
import { followUpView, type FollowState } from './creationFollowUp';
import { CardTitle } from '../ui';

/** A state in words, for the readers that do not see the colours. */
const STATE_WORDS: Record<FollowState, string> = {
  done: 'Fait',
  running: 'En cours',
  pending: 'En attente',
  failed: 'Échec',
};

/** A done or failed milestone fills its bar, a running one half. */
const SHARE: Record<FollowState, number> = {
  done: 1,
  running: 0.5,
  pending: 0,
  failed: 1,
};

/**
 * Whole minutes in words: « moins d’une minute », « 4 min », « 1 h 05 ».
 */
export function minutesWords(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return 'moins d’une minute';
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(
    2,
    '0',
  )}`;
}

/** « à l’instant », « il y a 4 min », « il y a 1 h 05 ». */
export function agoWords(ms: number): string {
  return ms < 60_000 ? 'à l’instant' : `il y a ${minutesWords(ms)}`;
}

function ExternalLink(props: { href: string; children: ReactNode }) {
  const iconsApi = useApi(iconsApiRef);
  return (
    <Link
      href={props.href}
      target="_blank"
      rel="noopener"
      variant="body-small"
      className="waadoo-preparing__link"
    >
      {props.children}{' '}
      <span className="waadoo-visually-hidden">(nouvel onglet)</span>
      <span className="waadoo-preparing__external" aria-hidden="true">
        {iconsApi.icon('external-link')}
      </span>
    </Link>
  );
}

function Milestone(props: {
  id: string;
  state: FollowState;
  icon: ReactNode;
  name: string;
  /** Its state words announced when they change (a polite live region). */
  live?: boolean;
  children: ReactNode;
}) {
  const { id, state, icon, name, live, children } = props;
  const Done = waadooIcons.stepDone;
  return (
    <li
      className="waadoo-service-milestone"
      data-milestone={id}
      data-state={state}
    >
      {/* Decorative: the state is said in words just after. */}
      <span className="waadoo-service-milestone__bar" aria-hidden>
        <span
          className="waadoo-service-milestone__fill"
          style={{ width: `${Math.round(SHARE[state] * 100)}%` }}
        />
      </span>
      <span className="waadoo-service-milestone__name">
        <span className="waadoo-service__glyph" aria-hidden="true">
          {state === 'done' ? <Done aria-hidden focusable="false" /> : icon}
        </span>
        {name}
        <span
          className="waadoo-visually-hidden"
          aria-live={live ? 'polite' : undefined}
        >
          {' — '}
          {STATE_WORDS[state]}
        </span>
      </span>
      {children}
    </li>
  );
}

const line = (content: ReactNode) => (
  <span className="waadoo-service-milestone__line">{content}</span>
);

/**
 * « Mise en service » of a project just created (mockup `create-B`): three
 * milestones — « Projet créé », « Premier build » (the latest pipeline of
 * `dev`), « Environnement dev » (its ArgoCD Application) —, in the look of
 * the ephemeral branch's card (`.waadoo-service-*`, BranchService.tsx); the
 * « Suivi automatique » pill while the page reads again on its own; « Ouvrir
 * le projet » and « Dépôt GitLab ».
 *
 * What is not read yet stays « en attente », with the template's link to
 * the pipelines; never an error. The address is plain text until the
 * environment is ready, then a link. When the id_token predates the
 * project's groups, the environment cannot be seen yet: said, with the
 * « Reconnexion nécessaire » alert of the environments card.
 *
 * The estimates: « ≈ 10 min au total » is the template's own (« Et
 * maintenant ? », « environ 10 minutes »); « ≈ 2 à 3 min » of start-up the
 * ephemeral branch card's, from its mockup — neither is measured.
 *
 * A `standard` project (point 8) has no build: no « Premier build », the
 * environment deploying until it is (`followUpView`).
 */
export function ProjectServiceCard(props: {
  /** The project's `AppType` when known (`taskTemplate.ts`), for its glyph. */
  appType: string | undefined;
  /** Its profile (`useProjectLifecycle`), undefined while unknown. */
  profile?: AppProfile;
  endedAt?: number;
  now: number;
  pipeline?: PipelineInfo;
  app?: ArgoApplication;
  following: boolean;
  staleAccess: boolean;
  pipelinesUrl?: string;
  devUrl?: string;
  projectHref?: string;
  repoUrl?: string;
}) {
  const {
    endedAt,
    now,
    pipeline,
    app,
    following,
    staleAccess,
    pipelinesUrl,
    projectHref,
    repoUrl,
  } = props;
  const iconsApi = useApi(iconsApiRef);
  const appLogo = useAppLogoKeys(props.appType);
  const titleId = useId();
  const view = followUpView(pipeline, app, props.profile);
  const standard = props.profile === 'standard';
  const devUrl = (app && applicationUrlOf(app)) ?? props.devUrl;
  const runId = pipeline ? pipelineNumber(pipeline.webUrl) : undefined;
  const buildMs = buildDurationMs(pipeline, now);
  const buildHref = pipeline?.webUrl ?? pipelinesUrl;

  let buildRun: string | undefined;
  if (pipeline) {
    const parts = [runId ? `pipeline #${runId}` : 'pipeline'];
    if (view.build === 'running' && buildMs !== undefined) {
      parts.push(`en cours depuis ${minutesWords(buildMs)}`);
    } else if (view.build === 'done' && buildMs !== undefined) {
      parts.push(`terminé en ${minutesWords(buildMs)}`);
    } else if (view.build === 'failed') {
      parts.push('en échec');
    } else if (pipeline.status === 'canceled') {
      parts.push('annulé');
    } else if (pipeline.status === 'skipped') {
      parts.push('ignoré');
    }
    buildRun = parts.join(' · ');
  } else if (view.build === 'pending') {
    buildRun = 'en attente du pipeline';
  }

  let environmentWords: string;
  if (view.environment === 'done') {
    environmentWords = 'prêt';
  } else if (view.environment === 'running') {
    environmentWords = 'déploiement en cours';
  } else if (view.build === 'failed') {
    environmentWords = 'en attente d’un build réussi';
  } else {
    environmentWords = 'en attente du build';
  }

  const ProjectIcon = waadooIcons.project;

  return (
    <Card className="waadoo-service-card">
      <CardHeader>
        <CardTitle
          level={3}
          titleId={titleId}
          icon={waadooIcons.deployment}
          title="Mise en service"
          actions={
            following && (
              <span className="waadoo-deletion-report__pill" data-tone="info">
                Suivi automatique
              </span>
            )
          }
        />
      </CardHeader>
      <CardBody>
        <div className="waadoo-creation-service">
          <ol className="waadoo-service-milestones" aria-labelledby={titleId}>
            <Milestone id="project" state="done" icon={null} name="Projet créé">
              {line('dépôts, groupes, environnements')}
              {endedAt !== undefined && line(agoWords(now - endedAt))}
            </Milestone>
            {!standard && (
              <Milestone
                id="build"
                state={view.build}
                icon={<ActionIcon name="gitlab" />}
                name="Premier build"
                live
              >
                {buildRun && line(buildRun)}
                {view.build !== 'done' && line('≈ 10 min au total')}
                {buildHref &&
                  line(
                    <ExternalLink href={buildHref}>
                      Suivre le build
                    </ExternalLink>,
                  )}
              </Milestone>
            )}
            <Milestone
              id="environment"
              state={view.environment}
              icon={<ActionIcon name="argocd" />}
              name="Environnement dev"
              live
            >
              {line(environmentWords)}
              {view.environment === 'pending' &&
                line('puis ≈ 2 à 3 min de démarrage')}
              {staleAccess &&
                view.environment !== 'done' &&
                line('visible ici après reconnexion')}
              {devUrl && (
                <span className="waadoo-service-milestone__line waadoo-service-milestone__address">
                  <span aria-hidden="true" className="waadoo-service__glyph">
                    {iconsApi.icon(appLogo.instance)}
                  </span>
                  {view.environment === 'done' ? (
                    <Link href={devUrl} target="_blank" rel="noopener">
                      {devUrl.replace(/^https?:\/\//, '')}
                    </Link>
                  ) : (
                    <span>{devUrl.replace(/^https?:\/\//, '')}</span>
                  )}
                </span>
              )}
            </Milestone>
          </ol>
          {staleAccess && <ReauthAlert />}
          {(projectHref || repoUrl) && (
            <div className="waadoo-creation-service__actions">
              {projectHref && (
                <ButtonLink
                  variant="primary"
                  href={projectHref}
                  iconStart={<ProjectIcon aria-hidden focusable="false" />}
                >
                  Ouvrir le projet
                </ButtonLink>
              )}
              {repoUrl && (
                <ButtonLink
                  variant="secondary"
                  href={repoUrl}
                  target="_blank"
                  rel="noopener"
                  iconStart={<ActionIcon name="gitlab" />}
                >
                  Dépôt GitLab
                </ButtonLink>
              )}
            </div>
          )}
          <Text
            as="p"
            variant="body-small"
            color="secondary"
            className="waadoo-service__hint"
          >
            {following
              ? 'L’adresse de l’environnement devient un lien dès qu’il est prêt. Rien à actualiser : cette carte se met à jour seule.'
              : 'L’adresse de l’environnement devient un lien dès qu’il est prêt.'}
          </Text>
        </div>
      </CardBody>
    </Card>
  );
}
